import { Reports } from './pages/Reports';
import { ArchiveEntry } from './pages/ArchiveEntry';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { LayoutWithProviders } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RouteErrorPage } from './components/ErrorBoundary';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AcceptInvite } from './pages/AcceptInvite';
import { Dashboard } from './pages/Dashboard';
import { Club } from './pages/Club';
import { Tastings } from './pages/Tastings';
import { EventDetail } from './pages/EventDetail';
import { TastingSetup } from './pages/TastingSetup';
import { LiveTasting } from './pages/LiveTasting';
import { Archive } from './pages/Archive';
import { ArchiveEvent } from './pages/ArchiveEvent';
import { Cellar } from './pages/Cellar';
import { WineDetail } from './pages/WineDetail';
import { Members } from './pages/Members';
import { MemberDetail } from './pages/MemberDetail';
import { Account } from './pages/Account';
import { Inbox } from './pages/Inbox';
import { Comms } from './pages/Comms';
import { CommsMessage } from './pages/CommsMessage';
import { Unsubscribe } from './pages/Unsubscribe';
import { Billing } from './pages/Billing';
import { InvoiceDetail } from './pages/InvoiceDetail';
import { Imports } from './pages/Imports';
import { ImportBatch } from './pages/ImportBatch';
import { NotFound } from './pages/NotFound';
import { HelpArticlePage, HelpIndex } from './pages/Help';
import { Contribute } from './pages/Contribute';
import { ContributionEdit } from './pages/ContributionEdit';
import { ContributionReview } from './pages/ContributionReview';
import { ContributionReviewDetail } from './pages/ContributionReviewDetail';
import { TastingDashboard } from './pages/TastingDashboard';
import { TastingWrapup } from './pages/TastingWrapup';
import { Setup } from './pages/Setup';

const router = createBrowserRouter([
  {
    element: <LayoutWithProviders />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/login', element: <Login /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/accept-invite', element: <AcceptInvite /> },
      // Public: an unsubscribe link must work without a login.
      { path: '/unsubscribe', element: <Unsubscribe /> },
      // Public: help must be reachable by someone who can't sign in.
      { path: '/help', element: <HelpIndex /> },
      { path: '/help/:slug', element: <HelpArticlePage /> },
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/dashboard', element: <Dashboard /> },
          { path: '/club', element: <Club /> },
          { path: '/tastings', element: <Tastings /> },
          { path: '/tastings/:id', element: <EventDetail /> },
          { path: '/live/:id', element: <LiveTasting /> },
          { path: '/cellar', element: <Cellar /> },
          { path: '/cellar/:id', element: <WineDetail /> },
          { path: '/archive', element: <Archive /> },
          {
            path: '/archive-entry',
            element: (
              <ProtectedRoute permission="records:contribute">
                <ArchiveEntry />
              </ProtectedRoute>
            ),
          },
          {
            path: '/reports',
            element: (
              <ProtectedRoute permission="records:approve">
                <Reports />
              </ProtectedRoute>
            ),
          },
          { path: '/archive/:id', element: <ArchiveEvent /> },
          { path: '/members', element: <Members /> },
          { path: '/members/:id', element: <MemberDetail /> },
          { path: '/account', element: <Account /> },
          { path: '/inbox', element: <Inbox /> },
          { path: '/billing/invoices/:invoiceId', element: <InvoiceDetail /> },
        ],
      },
      {
        // Every signed-in person may contribute history; the merge step protects the archive.
        element: <ProtectedRoute permission="history:contribute" />,
        children: [
          { path: '/contribute', element: <Contribute /> },
          {
            element: <ProtectedRoute permission="records:approve" />,
            children: [
              { path: '/contribute/review', element: <ContributionReview /> },
              { path: '/contribute/review/:id', element: <ContributionReviewDetail /> },
            ],
          },
          { path: '/contribute/:id', element: <ContributionEdit /> },
        ],
      },
      {
        element: <ProtectedRoute permission="payments:manage" />,
        children: [{ path: '/billing', element: <Billing /> }],
      },
      {
        element: <ProtectedRoute permission="comms:send" />,
        children: [
          { path: '/comms', element: <Comms /> },
          { path: '/comms/:id', element: <CommsMessage /> },
        ],
      },
      {
        element: <ProtectedRoute permission="tasting:create" />,
        children: [{ path: '/tastings/:id/setup', element: <TastingSetup /> }],
      },
      {
        element: <ProtectedRoute permission="tasting:run" />,
        children: [{ path: '/tastings/:id/dashboard', element: <TastingDashboard /> }],
      },
      {
        element: <ProtectedRoute permission="comms:event" />,
        children: [{ path: '/tastings/:id/wrapup', element: <TastingWrapup /> }],
      },
      {
        element: <ProtectedRoute permission="lookups:manage" />,
        children: [{ path: '/setup', element: <Setup /> }],
      },
      {
        element: <ProtectedRoute permission="import:run" />,
        children: [
          { path: '/imports', element: <Imports /> },
          { path: '/imports/:id', element: <ImportBatch /> },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
